import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Search,
    Users,
    AlertCircle,
    RefreshCw,
    Calendar,
    Filter,
    X,
    ChevronDown,
    CheckSquare,
} from 'lucide-react';
import patientCallingService, { PatientServiceLog } from '../../../../services/patientCalling.service';
import CommonPagination from '../../../Common/CommonPagination';
import styles from './ScheduleCampaignFlow.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApiFilters {
    search: string;
    dos_from: string;
    dos_to: string;
    insurance_names: string[]; // multi-select — sent as comma-separated
}

interface Props {
    callType: 'payment_reminder' | 'patient_info_gathering';
    selected: PatientServiceLog[];
    onChange: (records: PatientServiceLog[]) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CALL_TYPE_LABELS: Record<string, string> = {
    payment_reminder: 'Payment Reminder',
    patient_info_gathering: 'Patient Info Gathering',
};

/**
 * Only PENDING and FAILED records should be included in a new campaign.
 * This is always sent to the API — never filtered on the frontend.
 */
const ELIGIBLE_STATUSES = 'PENDING,FAILED';

const DEFAULT_PAGE_SIZE = 10;

const STATUS_BADGE_CLASS: Record<string, string> = {
    PENDING: styles.badgePending,
    FAILED: styles.badgeFailed,
    IN_PROGRESS: styles.badgeInProgress,
    COMPLETED: styles.badgeCompleted,
    VOICEMAIL: styles.badgeVoicemail,
    NO_ANSWER: styles.badgeNoAnswer,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, ms = 400): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const h = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(h);
    }, [value, ms]);
    return debounced;
}

function formatDate(val: string | null | undefined): string {
    if (!val) return '—';
    try {
        return new Date(val).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    } catch {
        return val;
    }
}

// ─── InsuranceDropdown ────────────────────────────────────────────────────────
// Multi-select dropdown. Options are derived from visible page records.
// Selecting a value re-fetches from the API with that filter applied.

interface InsuranceDropdownProps {
    options: string[];
    selected: string[];
    onChange: (v: string[]) => void;
    disabled?: boolean;
}

const InsuranceDropdown: React.FC<InsuranceDropdownProps> = ({
    options, selected, onChange, disabled,
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const toggle = (name: string) =>
        onChange(
            selected.includes(name)
                ? selected.filter(s => s !== name)
                : [...selected, name],
        );

    const label =
        selected.length === 0
            ? 'Insurance'
            : selected.length === 1
            ? selected[0]
            : `Insurance (${selected.length})`;

    return (
        <div ref={ref} className={styles.insuranceDropdownWrapper}>
            <button
                type="button"
                disabled={disabled}
                className={`${styles.insuranceTrigger} ${selected.length > 0 ? styles.insuranceTriggerActive : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                <Filter size={13} />
                <span className={styles.insuranceTriggerLabel}>{label}</span>
                <ChevronDown size={12} className={styles.insuranceTriggerChevron} />
            </button>

            {open && (
                <div className={styles.insuranceMenu}>
                    <div className={styles.insuranceMenuHeader}>
                        <span>Filter by Insurance</span>
                        {selected.length > 0 && (
                            <button
                                type="button"
                                className={styles.clearBtn}
                                onClick={() => onChange([])}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    <div className={styles.insuranceMenuList}>
                        {options.length === 0 ? (
                            <div className={styles.insuranceMenuEmpty}>
                                No insurance options on this page
                            </div>
                        ) : (
                            options.map(name => (
                                <label key={name} className={styles.insuranceMenuItem}>
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(name)}
                                        onChange={() => toggle(name)}
                                        className={styles.insuranceCheckbox}
                                    />
                                    <span className={styles.insuranceMenuLabel}>{name}</span>
                                </label>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── SkeletonRows ─────────────────────────────────────────────────────────────

const SkeletonRows: React.FC<{ count?: number }> = ({ count = 8 }) => (
    <>
        {Array.from({ length: count }).map((_, i) => (
            <tr key={`sk-${i}`} className={styles.skeletonRow}>
                <td className={styles.checkboxCell}>
                    <span className={`${styles.skeletonBar} ${styles.skeletonCheckbox}`} />
                </td>
                <td><span className={styles.skeletonBar} style={{ width: '72%' }} /></td>
                <td><span className={styles.skeletonBar} style={{ width: '58%' }} /></td>
                <td><span className={styles.skeletonBar} style={{ width: '80%' }} /></td>
                <td><span className={styles.skeletonBar} style={{ width: '48%' }} /></td>
                <td><span className={styles.skeletonBar} style={{ width: '55%' }} /></td>
                <td><span className={`${styles.skeletonBar} ${styles.skeletonPill}`} /></td>
            </tr>
        ))}
    </>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Step2RecordSelection: React.FC<Props> = ({ callType, selected, onChange }) => {
    // ── Server data ───────────────────────────────────────────────────────
    const [records, setRecords] = useState<PatientServiceLog[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // ── Pagination ────────────────────────────────────────────────────────
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    // ── Filters (all sent to API) ─────────────────────────────────────────
    const [filters, setFilters] = useState<ApiFilters>({
        search: '',
        dos_from: '',
        dos_to: '',
        insurance_names: [],
    });

    const debouncedSearch = useDebounce(filters.search, 400);

    // Reset page to 1 whenever any filter changes
    useEffect(() => { setPage(1); }, [
        debouncedSearch,
        filters.dos_from,
        filters.dos_to,
        filters.insurance_names,
    ]);

    // ── Fetch — ALWAYS server-side, ALWAYS pass call_type + call_status ───
    const fetchRecords = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: Record<string, string | number | undefined> = {
                page,
                page_size: pageSize,
                // ✅ Server-side call_type filter (from Step 1)
                call_type: callType,
                // ✅ Server-side status filter — NEVER fetch all statuses
                call_status: ELIGIBLE_STATUSES,
            };

            // ✅ Search — server-side
            if (debouncedSearch.trim()) {
                params.search = debouncedSearch.trim();
            }

            // ✅ Date range — server-side
            if (filters.dos_from) params.dos_from = filters.dos_from;
            if (filters.dos_to)   params.dos_to   = filters.dos_to;

            // ✅ Insurance — server-side (comma-separated)
            if (filters.insurance_names.length > 0) {
                params.insurance_name = filters.insurance_names.join(',');
            }

            const res = await patientCallingService.getAllRecords(params);

            setRecords(res.data ?? []);
            setTotal(res.total ?? 0);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load records';
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [callType, page, pageSize, debouncedSearch, filters.dos_from, filters.dos_to, filters.insurance_names]);

    useEffect(() => { fetchRecords(); }, [fetchRecords]);

    // ── Insurance options — derived from current page's records ───────────
    // These are used to populate the dropdown, but the actual filtering
    // happens server-side when the user picks one.
    const insuranceOptions = useMemo(() => {
        const names = records
            .map(r => r.insurance_name)
            .filter((n): n is string => !!n && n.trim() !== '');
        return Array.from(new Set(names)).sort();
    }, [records]);

    // ── Selection helpers ─────────────────────────────────────────────────
    const selectedIds = useMemo(() => new Set(selected.map(s => s.id)), [selected]);

    const isAllPageSelected =
        records.length > 0 && records.every(r => selectedIds.has(r.id));

    const isSomePageSelected =
        !isAllPageSelected && records.some(r => selectedIds.has(r.id));

    const toggleRecord = (record: PatientServiceLog) => {
        if (selectedIds.has(record.id)) {
            onChange(selected.filter(s => s.id !== record.id));
        } else {
            onChange([...selected, record]);
        }
    };

    const togglePage = () => {
        if (isAllPageSelected) {
            const pageIds = new Set(records.map(r => r.id));
            onChange(selected.filter(s => !pageIds.has(s.id)));
        } else {
            const toAdd = records.filter(r => !selectedIds.has(r.id));
            onChange([...selected, ...toAdd]);
        }
    };

    // ── Filter helpers ────────────────────────────────────────────────────
    const setFilter = <K extends keyof ApiFilters>(key: K, val: ApiFilters[K]) =>
        setFilters(prev => ({ ...prev, [key]: val }));

    const hasActiveFilters =
        filters.insurance_names.length > 0 ||
        !!filters.dos_from ||
        !!filters.dos_to;

    const clearFilters = () =>
        setFilters(prev => ({
            ...prev,
            dos_from: '',
            dos_to: '',
            insurance_names: [],
        }));

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    // ─── Render ───────────────────────────────────────────────────────────

    return (
        <div className={styles.stepContent}>
            {/* Header */}
            <div className={styles.stepHeading}>
                <Users size={20} className={styles.stepHeadingIcon} />
                <div>
                    <h3 className={styles.stepTitle}>Select Patient Records</h3>
                    <p className={styles.stepSubtitle}>
                        Choose which patients to include in this{' '}
                        <strong>{CALL_TYPE_LABELS[callType] ?? callType}</strong> campaign.
                        Only <span className={styles.statusHint}>Pending</span> and{' '}
                        <span className={styles.statusHint}>Failed</span> records are shown.
                    </p>
                </div>
            </div>

            {/* ── Filter Row ── */}
            <div className={styles.filterRow}>
                {/* Search */}
                <div className={styles.searchWrapper}>
                    <Search size={14} className={styles.searchIcon} />
                    <input
                        type="text"
                        className={styles.searchInput2}
                        placeholder="Search by name or phone…"
                        value={filters.search}
                        onChange={e => setFilter('search', e.target.value)}
                    />
                    {filters.search && (
                        <button
                            type="button"
                            className={styles.clearSearchBtn}
                            onClick={() => setFilter('search', '')}
                        >
                            <X size={13} />
                        </button>
                    )}
                </div>

                {/* Insurance multi-select */}
                <InsuranceDropdown
                    options={insuranceOptions}
                    selected={filters.insurance_names}
                    onChange={v => setFilter('insurance_names', v)}
                    disabled={loading}
                />

                {/* Date range */}
                <div className={styles.dateRangeWrapper}>
                    <Calendar size={13} className={styles.dateRangeIcon} />
                    <input
                        type="date"
                        className={styles.dateInput}
                        value={filters.dos_from}
                        onChange={e => setFilter('dos_from', e.target.value)}
                        title="DOS from"
                    />
                    <span className={styles.dateRangeSep}>–</span>
                    <input
                        type="date"
                        className={styles.dateInput}
                        value={filters.dos_to}
                        onChange={e => setFilter('dos_to', e.target.value)}
                        title="DOS to"
                    />
                </div>

                {hasActiveFilters && (
                    <button
                        type="button"
                        className={styles.clearFiltersBtn}
                        onClick={clearFilters}
                    >
                        <X size={12} /> Clear filters
                    </button>
                )}

                <button
                    type="button"
                    className={styles.refreshBtn}
                    onClick={fetchRecords}
                    title="Refresh"
                    disabled={loading}
                >
                    <RefreshCw size={13} className={loading ? styles.spinning : ''} />
                </button>
            </div>

            {/* ── Selection Summary Bar ── */}
            <div className={styles.selectionBar}>
                <div className={styles.selectionLeft}>
                    {selected.length > 0 ? (
                        <>
                            <CheckSquare size={14} className={styles.selectionIcon} />
                            <strong>{selected.length}</strong>&nbsp;
                            patient{selected.length !== 1 ? 's' : ''} selected
                            <button
                                type="button"
                                className={styles.deselectAllBtn}
                                onClick={() => onChange([])}
                            >
                                <X size={11} /> Deselect all
                            </button>
                        </>
                    ) : (
                        <span className={styles.selectionPlaceholder}>
                            No patients selected yet
                        </span>
                    )}
                </div>
                <span className={styles.selectionMeta}>
                    {loading ? 'Loading…' : `${total.toLocaleString()} records match filters`}
                </span>
            </div>

            {/* ── Table ── */}
            <div className={styles.tableWrapper}>
                <table className={styles.selectionTable} aria-busy={loading}>
                    <thead>
                        <tr>
                            <th className={styles.checkboxCell}>
                                {/* Select-all for current page */}
                                <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    checked={isAllPageSelected}
                                    disabled={loading || records.length === 0}
                                    ref={el => {
                                        if (el) el.indeterminate = isSomePageSelected;
                                    }}
                                    onChange={togglePage}
                                    title="Select / deselect all on this page"
                                />
                            </th>
                            <th>Patient Name</th>
                            <th>Phone</th>
                            <th>Insurance</th>
                            <th>DOS</th>
                            <th>Call Type</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Loading */}
                        {loading && <SkeletonRows count={pageSize} />}

                        {/* Error */}
                        {!loading && error && (
                            <tr>
                                <td colSpan={7}>
                                    <div className={styles.tableError}>
                                        <AlertCircle size={17} />
                                        <span>{error}</span>
                                        <button
                                            type="button"
                                            className={styles.retryBtn}
                                            onClick={fetchRecords}
                                        >
                                            <RefreshCw size={12} /> Retry
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {/* Empty */}
                        {!loading && !error && records.length === 0 && (
                            <tr>
                                <td colSpan={7}>
                                    <div className={styles.tableEmpty}>
                                        <Users size={36} />
                                        <p>No records found</p>
                                        <span>
                                            {filters.search || hasActiveFilters
                                                ? 'Try adjusting your search or filters'
                                                : `No eligible records for ${CALL_TYPE_LABELS[callType] ?? callType}`}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {/* Rows */}
                        {!loading && !error && records.map(record => {
                            const isSelected = selectedIds.has(record.id);
                            const statusClass =
                                STATUS_BADGE_CLASS[record.call_status ?? ''] ?? styles.badgePending;

                            return (
                                <tr
                                    key={record.id}
                                    className={`${styles.tableRow} ${isSelected ? styles.tableRowSelected : ''}`}
                                    onClick={() => toggleRecord(record)}
                                >
                                    <td
                                        className={styles.checkboxCell}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <input
                                            type="checkbox"
                                            className={styles.checkbox}
                                            checked={isSelected}
                                            onChange={() => toggleRecord(record)}
                                        />
                                    </td>
                                    <td>
                                        <span className={styles.patientName}>
                                            {record.patient_name || '—'}
                                        </span>
                                    </td>
                                    <td>
                                        {record.phone_number
                                            ? <span className={styles.phone}>{record.phone_number}</span>
                                            : <em className={styles.na}>N/A</em>}
                                    </td>
                                    <td>
                                        {record.insurance_name
                                            ? <span className={styles.insurance}>{record.insurance_name}</span>
                                            : <em className={styles.na}>N/A</em>}
                                    </td>
                                    <td>
                                        <span className={styles.dos}>{formatDate(record.dos)}</span>
                                    </td>
                                    <td>
                                        {record.call_type ? (
                                            <span
                                                className={`${styles.callTypeBadge} ${record.call_type === 'payment_reminder' ? styles.badgeGreen : styles.badgePurple}`}
                                            >
                                                {CALL_TYPE_LABELS[record.call_type] ?? record.call_type}
                                            </span>
                                        ) : (
                                            <em className={styles.na}>—</em>
                                        )}
                                    </td>
                                    <td>
                                        {record.call_status ? (
                                            <span className={`${styles.statusBadge} ${statusClass}`}>
                                                {record.call_status}
                                            </span>
                                        ) : (
                                            <em className={styles.na}>—</em>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ── Pagination ── */}
            {!loading && !error && total > 0 && (
                <CommonPagination
                    show
                    renderInPlace
                    pageCount={totalPages}
                    currentPage={page - 1}
                    onPageChange={({ selected: s }) => setPage(s + 1)}
                    totalItems={total}
                    itemsPerPage={pageSize}
                    onItemsPerPageChange={size => {
                        setPageSize(size);
                        setPage(1);
                    }}
                />
            )}

            {/* ── Validation hint ── */}
            {selected.length === 0 && !loading && (
                <p className={styles.validationHint}>
                    Select at least one patient record to continue.
                </p>
            )}
        </div>
    );
};

export default Step2RecordSelection;